import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { Button } from '../ui';
import styles from './SignupSurveyModal.module.css';

interface SurveyOption {
    value: string;
    label: string;
}

type Questions = Record<string, SurveyOption[]>;

interface Answers {
    objetivo: string[];
    objetivoOutro: string;
    controleFinanceiro: string;
    controleFinanceiroOutro: string;
    segmento: string;
    segmentoOutro: string;
    faturamentoAnual: string;
    numeroFuncionarios: string;
    comoConheceu: string[];
    comoConheceuOutro: string;
}

type AnswerKey = keyof Answers;

interface StepMeta {
    key: AnswerKey;
    outroKey?: AnswerKey;
    question: string;
    subtitle?: string;
    multi: boolean;
}

const STEPS: StepMeta[] = [
    { key: 'objetivo', outroKey: 'objetivoOutro', question: 'O que você mais quer resolver agora?', subtitle: 'Selecione até 3 opções', multi: true },
    { key: 'controleFinanceiro', outroKey: 'controleFinanceiroOutro', question: 'Como você controla suas finanças hoje?', multi: false },
    { key: 'segmento', outroKey: 'segmentoOutro', question: 'Qual o segmento do seu negócio?', multi: false },
    { key: 'faturamentoAnual', question: 'Qual o faturamento anual da sua empresa?', multi: false },
    { key: 'numeroFuncionarios', question: 'Quantos funcionários têm na sua empresa?', multi: false },
    { key: 'comoConheceu', outroKey: 'comoConheceuOutro', question: 'Como você conheceu o Financeiro Diário?', subtitle: 'Selecione até 3 opções', multi: true },
];

const MAX_MULTI = 3;
const OUTRO_VALUE = 'outro';

const INITIAL_ANSWERS: Answers = {
    objetivo: [],
    objetivoOutro: '',
    controleFinanceiro: '',
    controleFinanceiroOutro: '',
    segmento: '',
    segmentoOutro: '',
    faturamentoAnual: '',
    numeroFuncionarios: '',
    comoConheceu: [],
    comoConheceuOutro: '',
};

export function SignupSurveyModal() {
    const { user, updateUser } = useAuth();
    const [questions, setQuestions] = useState<Questions | null>(null);
    const [step, setStep] = useState(0); // 0 = tela de intro, 1..STEPS.length = perguntas
    const [answers, setAnswers] = useState<Answers>(INITIAL_ANSWERS);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const shouldShow = !!user
        && user.role !== 'ADMIN'
        && user.status !== 'suspended'
        && !!user.surveyOnboardingRequired
        && !user.surveyOnboardingCompletedAt;

    useEffect(() => {
        if (!shouldShow || questions) return;
        api.getOnboardingSurveyQuestions().then(setQuestions).catch(() => { /* silently ignore */ });
    }, [shouldShow, questions]);

    const totalSteps = STEPS.length;
    const currentMeta = step > 0 ? STEPS[step - 1] : null;
    const currentOptions = useMemo(
        () => (currentMeta && questions ? questions[currentMeta.key] ?? [] : []),
        [currentMeta, questions],
    );

    if (!shouldShow || !questions) return null;

    const isMultiSelected = (key: AnswerKey, value: string) => (answers[key] as string[]).includes(value);

    const toggleMulti = (key: AnswerKey, value: string) => {
        setAnswers(prev => {
            const current = prev[key] as string[];
            if (current.includes(value)) {
                return { ...prev, [key]: current.filter(v => v !== value) };
            }
            if (current.length >= MAX_MULTI) return prev;
            return { ...prev, [key]: [...current, value] };
        });
    };

    const setSingle = (key: AnswerKey, value: string) => {
        setAnswers(prev => ({ ...prev, [key]: value }));
    };

    const isValidStep = (() => {
        if (!currentMeta) return true;
        const value = answers[currentMeta.key];
        if (currentMeta.multi) return (value as string[]).length > 0;
        if (!value) return false;
        // Se selecionou "Outro" na pergunta de segmento, obrigar a preencher o campo livre
        if (currentMeta.key === 'segmento' && value === OUTRO_VALUE) {
            return !!String(answers[currentMeta.outroKey as AnswerKey] ?? '').trim();
        }
        return true;
    })();

    const showOutroInput = !!currentMeta?.outroKey && (
        currentMeta.multi ? isMultiSelected(currentMeta.key, OUTRO_VALUE) : answers[currentMeta.key] === OUTRO_VALUE
    );

    const handleNext = async () => {
        if (!currentMeta) {
            setStep(1);
            return;
        }
        if (step < totalSteps) {
            setStep(s => s + 1);
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await api.submitOnboardingSurvey({
                objetivo: answers.objetivo,
                objetivoOutro: answers.objetivoOutro || undefined,
                controleFinanceiro: answers.controleFinanceiro,
                controleFinanceiroOutro: answers.controleFinanceiroOutro || undefined,
                segmento: answers.segmento,
                segmentoOutro: answers.segmentoOutro || undefined,
                faturamentoAnual: answers.faturamentoAnual,
                numeroFuncionarios: answers.numeroFuncionarios,
                comoConheceu: answers.comoConheceu,
                comoConheceuOutro: answers.comoConheceuOutro || undefined,
            });
            updateUser({ surveyOnboardingCompletedAt: new Date().toISOString() });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Não foi possível enviar suas respostas. Tente novamente.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => setStep(s => Math.max(0, s - 1));

    return (
        <div className={`${styles.overlay} notranslate`} translate="no">
            <div className={styles.modal}>
                <div className={styles.header}>
                    <span className={styles.stepLabel}>{step === 0 ? 'Complete seu cadastro' : 'Perfil do negócio'}</span>
                    <span className={styles.stepCount}>{step}/{totalSteps}</span>
                </div>

                <div className={styles.progressTrack}>
                    <div className={styles.progressFill} style={{ width: `${(step / totalSteps) * 100}%` }} />
                </div>

                {!currentMeta ? (
                    <div className={styles.introBody}>
                        <h2 className={styles.introTitle}>
                            Queremos entender <strong>seu negócio</strong> para aprimorar a plataforma!
                        </h2>
                        <p className={styles.introText}>
                            As perguntas são sobre seu negócio e ajudarão a entender de forma mais assertiva
                            como melhorar a plataforma e oferecer a melhor experiência possível.
                        </p>
                    </div>
                ) : (
                    <div className={styles.body}>
                        <h2 className={styles.question}>{currentMeta.question}</h2>
                        {currentMeta.subtitle && <p className={styles.subtitle}>{currentMeta.subtitle}</p>}

                        <div className={styles.optionsGrid}>
                            {currentOptions.map(opt => {
                                const active = currentMeta.multi
                                    ? isMultiSelected(currentMeta.key, opt.value)
                                    : answers[currentMeta.key] === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        className={`${styles.optionChip} ${active ? styles.optionChipActive : ''}`}
                                        onClick={() => currentMeta.multi ? toggleMulti(currentMeta.key, opt.value) : setSingle(currentMeta.key, opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>

                        {showOutroInput && currentMeta.outroKey && (
                            <textarea
                                className={styles.outroInput}
                                placeholder="Conte pra gente..."
                                rows={3}
                                value={answers[currentMeta.outroKey] as string}
                                onChange={e => setAnswers(prev => ({ ...prev, [currentMeta.outroKey as AnswerKey]: e.target.value }))}
                            />
                        )}
                    </div>
                )}

                {error && <p className={styles.error}>{error}</p>}

                <div className={styles.footer}>
                    {step > 0
                        ? <button type="button" className={styles.backLink} onClick={handleBack} disabled={submitting}>Voltar</button>
                        : <span />}

                    <Button onClick={handleNext} disabled={!isValidStep || submitting} isLoading={submitting}>
                        {step === 0 ? 'Começar' : step === totalSteps ? 'Concluir' : 'Seguir'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
